// Shared frontend types

/** Audio data computed per frame, feeding the visuals. */
export interface AudioFrame {
  /** Frequency spectrum normalized 0..1 (Uint8 / 255 converted to float). */
  frequencies: Float32Array
  /** Waveform (time domain) normalized -1..1. */
  waveform: Float32Array
  /** Energy per band, 0..1 */
  bass: number
  mid: number
  treble: number
  /** Global volume/energy 0..1 (smoothed RMS). */
  level: number
  /** true on the frame where a beat is detected. */
  beat: boolean
  /** Intensity of the last beat 0..1 (decays over time). */
  beatIntensity: number
  /** Estimated tempo of the track in beats per minute (smoothed). */
  bpm: number
}

export type AudioSourceKind = 'none' | 'file' | 'loopback' | 'mic'

/** Which top-level app mode is active. */
export type AppMode = 'menu' | 'live' | 'studio'

/** Video layer relative to the shape (used for flat background). */
export type VideoLayer = 'back' | 'front'

/**
 * Background video placement. Independent from the primitive: you can show a
 * background AND a primitive at the same time.
 *   - 'off'    : no background video (the primitive can still use it, e.g. LED).
 *   - 'flat'   : flat fullscreen background.
 *   - 'sphere' : navigable 360 sphere.
 */
export type VideoBackground = 'off' | 'flat' | 'sphere' | 'tunnel'

/** Mirror / kaleidoscope effect applied to the background video. */
export type VideoMirror = 'none' | 'horizontal' | 'vertical' | 'quad' | 'kaleido'

/** Texture source painted inside the primitive's pixels. */
export type TextureSource = 'none' | 'video' | 'image'

/** Sprite shape used to draw each point in the point-based primitives. */
export type PointShape = 'circle' | 'square' | 'triangle'

/** Metaballs look/behaviour variant. */
export type MetaVariant = 'mercury' | 'energy' | 'fission' | 'spectrum' | 'lava'

/**
 * Layout of the frequency spectrum bars:
 * - `linear`: a straight, upright row of bars facing the camera (classic equalizer)
 * - `mirror`: a straight row mirrored above and below a center baseline
 * - `arc`: bars arranged on a 180° curved wall, standing up, facing the camera
 * - `radial`: a full 360° ring of bars (circular equalizer)
 */
export type SpectrumStyle = 'linear' | 'mirror' | 'arc' | 'radial'

/**
 * LED panel shape:
 *  - 'plane'    : flat 16:9 panel.
 *  - 'curve'    : concave 180° curved wall (panoramic).
 *  - 'sphere'   : 360° sphere (camera inside, equirectangular video).
 *  - 'cylinder' : 360° LED wall (camera inside).
 */
export type LedShape = 'plane' | 'curve' | 'sphere' | 'cylinder'

/** How the video wraps over the LED surface. */
export type ContentWrap = 'full' | 'repeat' | 'mirror' | 'kaleido'

/** What the background is: nothing, a solid/gradient color, an image, or a video. */
export type BackgroundType = 'off' | 'color' | 'image' | 'video'

/** Color background: a single solid color or a multi-stop gradient. */
export type BgColorMode = 'solid' | 'gradient'

/** Gradient shape for the color background. */
export type GradientType = 'linear' | 'radial'

/**
 * Background video playback style:
 *  - 'linear'       : normal loop.
 *  - 'acceleration' : speeds up on each beat, eases back down (subtle decel).
 *  - 'pingpong'     : plays the video forward then backward, beat-driven speed.
 */
export type VideoLoopStyle = 'linear' | 'acceleration' | 'pingpong' | 'crossfade'

/**
 * Camera behaviour. Beyond static/auto-rotate/cinematic, the motion modes
 * (spiral / back-and-forth / left-right / up-down) move in sync with the beat,
 * driven by the estimated BPM.
 */
export type CameraMode =
  | 'static'
  | 'free'
  | 'autorotate'
  | 'cinematic'
  | 'spiral'
  | 'backforth'
  | 'leftright'
  | 'updown'

/** Saved camera pose (position + look target + up vector) for scenes. */
export interface CameraPose {
  position: [number, number, number]
  target: [number, number, number]
  up: [number, number, number]
}

export type PrimitiveKind =
  | 'pointcloud'
  | 'lines'
  | 'triangles'
  | 'plane'
  | 'waveform'
  | 'constellation'
  | 'tunnel'
  | 'vortex'
  | 'ledpanel'
  | 'fluid'
  | 'fireworks'
  | 'rain'
  | 'smoke'
  | 'plasma'
  | 'metaballs'
  | 'spectrum'
  | 'galaxy'
  | 'aurora'
  | 'ferro'
  | 'videodisplace'

/** Audio band that drives an effect's intensity. */
export type DisplaceBand = 'level' | 'bass' | 'mid' | 'treble'

/** Editable parameters of the visuals scene. */
export interface VisualParams {
  primitive: PrimitiveKind
  /**
   * Whether the selected primitive is drawn. When turned off (2nd click) no
   * primitive is rendered; if there's a video, it goes fullscreen.
   */
  primitiveEnabled: boolean
  /** Sensitivity to audio reactivity. */
  reactivity: number
  /** Camera/scene rotation speed. */
  rotationSpeed: number
  /** Base hue 0..1. */
  hue: number
  /** How much the bass displaces the geometry. */
  bassPunch: number
  /** Beat impulse attack time (seconds): how long the push takes to ease IN. */
  beatAttack: number
  /** Beat impulse release time (seconds): how long the push takes to ease OUT. */
  beatRelease: number
  /**
   * Random deformation: when on, each percussive hit (snare/boom) punches a fresh
   * random deformation of the primitive. When off, the primitive is undeformed
   * (looks like the original).
   */
  seedAuto: boolean
  /** Density of points/elements. */
  density: number
  /** Point size multiplier. */
  pointSize: number
  /** Sprite shape of each point (circle / square / triangle). */
  pointShape: PointShape
  /** Metaballs variant (mercury / energy / fission / spectrum / lava). */
  metaVariant: MetaVariant
  /** Spectrum layout (linear / mirror / arc / radial). */
  spectrumStyle: SpectrumStyle
  /** Opaque points (hard edge, occluding) vs the default translucent glow. */
  pointSolid: boolean
  /** Line width in CSS pixels (only the "lines" primitive). */
  lineWidth: number
  /** Frame persistence (trail). 0 = no trail. */
  trails: number
  /** Bloom/glow intensity. 0 = no glow. */
  bloom: number
  /** Camera behaviour (static / auto-rotate / cinematic / beat-synced motions). */
  cameraMode: CameraMode
  /** Beats between camera-motion pose changes (e.g. back ↔ forth every N beats). */
  cameraBeats: number
  /** Camera transition ease-in (0..1: slow start). */
  cameraEaseIn: number
  /** Camera transition ease-out (0..1: slow end). */
  cameraEaseOut: number
  /** Camera transition speed multiplier (higher = snappier moves). */
  cameraSpeed: number
  /** Video layer: behind (background) or in front (overlay with transparent blacks). */
  videoLayer: VideoLayer
  /** Background video placement: off / flat / 360 sphere (independent of the primitive). */
  videoBackground: VideoBackground
  /** Mirror/kaleidoscope effect applied to the background video. */
  videoMirror: VideoMirror
  /** Mirror density multiplier (number of folds / kaleidoscope segments). */
  videoMirrorAmount: number
  /** 360 sphere "size": UV zoom of the panorama (>1 = bigger sphere / zoom out). */
  videoSphereScale: number
  /** Background video opacity (0..1). */
  videoOpacity: number
  /** Texture painted inside the primitive's pixels (video/image/none). */
  textureSource: TextureSource
  /** LED panel shape: flat / 180° curve / 360° sphere / 360° cylinder. */
  ledShape: LedShape
  /** Radius of the wrap-around LED shapes (sphere/cylinder); camera sits inside. */
  sphereRadius: number
  /** Video repetitions across the LED surface (more = smaller content). */
  contentTiles: number
  /** How the video wraps over the LED surface (full / repeat / mirror / kaleido). */
  contentWrap: ContentWrap
  /** What the background is: off / color / image / video. */
  backgroundType: BackgroundType
  /** Color background: solid or gradient. */
  bgColorMode: BgColorMode
  /** Gradient shape (when bgColorMode is 'gradient'). */
  bgGradientType: GradientType
  /** Linear gradient angle in degrees. */
  bgGradientAngle: number
  /** Color stops for the color background (solid uses the first). */
  bgColorStops: string[]
  /** Background video playback style (linear / acceleration / ping-pong). */
  videoLoopStyle: VideoLoopStyle
  /** Acceleration impulse: peak speed, duration (s), easing (0..1 peakiness). */
  accelSpeed: number
  accelDuration: number
  accelEase: number
  /** Ping (forward) phase: peak speed + easing (bounces at the video ends). */
  pingSpeed: number
  pingEase: number
  /** Pong (backward) phase: peak speed + easing. */
  pongSpeed: number
  pongEase: number
  /** Crossfade loop: dissolve duration (seconds) over the loop seam so the jump
   *  from the video's end back to its start isn't felt. */
  loopBlend: number

  // --- Video displacement field ('videodisplace' primitive) ---
  /** Displacement magnitude: how far bright/dark areas push the points. */
  displaceScale: number
  /** Luminance midpoint (0..1): above it points push out (+Z), below it sink in. */
  displaceThreshold: number
  /** How much the audio modulates the displacement (0 = static relief). */
  displaceAudio: number
  /** Which audio band drives the displacement (level / bass / mid / treble). */
  displaceBand: DisplaceBand
}

export const DEFAULT_VISUAL_PARAMS: VisualParams = {
  primitive: 'pointcloud',
  primitiveEnabled: true,
  reactivity: 1.0,
  rotationSpeed: 0.15,
  hue: 0.55,
  bassPunch: 1.0,
  beatAttack: 0.07,
  beatRelease: 0.22,
  seedAuto: false,
  density: 1.0,
  pointSize: 1.0,
  pointShape: 'circle',
  pointSolid: false,
  metaVariant: 'mercury',
  spectrumStyle: 'linear',
  lineWidth: 2.0,
  trails: 0.0,
  bloom: 0.7,
  cameraMode: 'autorotate',
  cameraBeats: 4,
  cameraEaseIn: 0.4,
  cameraEaseOut: 0.4,
  cameraSpeed: 1,
  videoLayer: 'back',
  videoBackground: 'flat',
  videoMirror: 'none',
  videoMirrorAmount: 2,
  videoSphereScale: 1,
  videoOpacity: 1,
  textureSource: 'none',
  ledShape: 'plane',
  sphereRadius: 8,
  contentTiles: 1,
  contentWrap: 'full',
  backgroundType: 'video',
  bgColorMode: 'gradient',
  bgGradientType: 'linear',
  bgGradientAngle: 90,
  bgColorStops: ['#0d0221', '#7b2cbf'],
  videoLoopStyle: 'linear',
  accelSpeed: 3,
  accelDuration: 0.4,
  accelEase: 0.5,
  pingSpeed: 2,
  pingEase: 0.5,
  pongSpeed: 2,
  pongEase: 0.5,
  loopBlend: 0.6,
  displaceScale: 0.9,
  displaceThreshold: 0.5,
  displaceAudio: 1.0,
  displaceBand: 'level',
}

/**
 * Video Lab settings: the selected video (grayscale) is remapped
 * to a live palette (gray → color ID → palette color). The video SOURCE
 * is the active video from the catalog (`backgroundVideoUrl`); only the
 * recoloring look and whether it's active live here.
 */
export interface VideoLabSettings {
  /** Active palette: hex colors in order. */
  palette: string[]
  /** Number of bands / color IDs in discrete mode. */
  bands: number
  /** true = smooth gradient; false = discrete bands. */
  smooth: boolean
  /** Gray↔color mix (0..1). */
  intensity: number
  /** Black point of the levels remap (0..1). */
  black: number
  /** White point of the levels remap (0..1). */
  white: number
  /** Edge smoothing (0..1): blurs the luminance before quantizing to clean band edges. */
  smoothing: number
  /** How the palette color combines with the source brightness. */
  blend: BlendMode
  /** Whether the recoloring is applied to the scene. */
  active: boolean
}

/** Blend mode for combining the palette color with the source. */
export type BlendMode = 'normal' | 'multiply' | 'screen' | 'add'

/** Shape drawn for a Zone-reaction band. */
export type ZoneShapeType = 'circle' | 'square' | 'triangle' | 'image'

/** The 4 rhythm bands (match the player meter): bass, mid, treble, level. */
export const ZONE_BAND_LABELS = ['Bass', 'Mid', 'Treble', 'Level'] as const

/** Per-band Zone-reaction configuration. */
export interface ZoneBand {
  /** How many shapes of this band travel the surface. */
  count: number
  /** Shape drawn for this band. */
  shape: ZoneShapeType
  /** Image painted inside the shapes when `shape === 'image'`. */
  imageUrl: string | null
  /** Zone radius on the surface (angular, radians). */
  size: number
}

/**
 * Zone reaction: shapes tied to each rhythm band that travel the primitive's
 * surface and deform the mesh locally on their band's hit. When off, the
 * primitive behaves exactly as before (global random deform).
 */
export interface ZoneReactionConfig {
  enabled: boolean
  /** Active bands, 1..4 (index 0 bass, 1 mid, 2 treble, 3 level). */
  bands: number
  /** Surface travel speed. */
  speed: number
  /** "random" end of the speed control: per-shape randomized speed/direction. */
  speedRandom: boolean
  /** Derive the travel speed from the track BPM (overrides `speed`). */
  bpmSync: boolean
  /** Draw the zone shapes (false = invisible markers; the deformation still runs). */
  showShapes: boolean
  /** Length-4 per-band config; the first `bands` are active. */
  perBand: ZoneBand[]
}

export const DEFAULT_ZONE_REACTION: ZoneReactionConfig = {
  enabled: false,
  bands: 2,
  speed: 1,
  speedRandom: false,
  bpmSync: false,
  showShapes: true,
  perBand: [
    { count: 2, shape: 'circle', imageUrl: null, size: 0.35 },
    { count: 3, shape: 'triangle', imageUrl: null, size: 0.3 },
    { count: 2, shape: 'square', imageUrl: null, size: 0.3 },
    { count: 1, shape: 'circle', imageUrl: null, size: 0.4 },
  ],
}

export const DEFAULT_VIDEO_LAB: VideoLabSettings = {
  palette: ['#0d0221', '#7b2cbf', '#e0aaff', '#ffd166'],
  bands: 4,
  smooth: false,
  intensity: 1.0,
  black: 0.0,
  white: 1.0,
  smoothing: 0.0,
  blend: 'normal',
  active: true, // colors on by default (the background recolor IS the feature)
}

/**
 * Primitive recolor defaults. Unlike the background, the primitive already has
 * its own vibrant, audio-reactive coloring, so the palette recolor is OFF by
 * default (opt-in) — turning it on replaces that look with the palette.
 */
export const DEFAULT_PRIMITIVE_LAB: VideoLabSettings = {
  ...DEFAULT_VIDEO_LAB,
  active: false,
}

// --- Scene transitions ------------------------------------------------------

/**
 * Transition style played when switching scenes. The "modern" pool (glitch,
 * rgbShift, zoomBlur, whipPan, displace, ripple, burn, spin, pixelate) masks the
 * cut with motion; the rest are tasteful simple ones (crossfade, lumaWipe, bars,
 * iris, dip).
 */
export const TRANSITION_TYPES = [
  'crossfade',
  'dip',
  'lumaWipe',
  'bars',
  'iris',
  'pixelate',
  'glitch',
  'rgbShift',
  'zoomBlur',
  'whipPan',
  'displace',
  'ripple',
  'burn',
  'spin',
] as const
export type TransitionType = (typeof TRANSITION_TYPES)[number]

/** Direction for directional transitions (wipe/bars/whip) and in/out for iris/zoom. */
export type TransitionDirection = 'left' | 'right' | 'up' | 'down' | 'in' | 'out'

/** Persisted transition preferences (the panel state). */
export interface TransitionSettings {
  /** Play a transition when a scene is loaded (manually or by the Director). */
  enabled: boolean
  /** Selected style, or 'random' to pick a fresh one each time. */
  type: TransitionType | 'random'
  /** Mix duration in milliseconds. */
  durationMs: number
  /** Effect strength (displacement/blur/chroma amount). */
  intensity: number
  /** Direction for the directional styles. */
  direction: TransitionDirection
}

export const DEFAULT_TRANSITION: TransitionSettings = {
  enabled: true,
  type: 'glitch',
  durationMs: 600,
  intensity: 1,
  direction: 'right',
}

/** A resolved transition (no 'random'), passed to the renderer. */
export interface TransitionConfig {
  type: TransitionType
  durationMs: number
  intensity: number
  direction: TransitionDirection
}

/**
 * The full visual state of a scene, enough to reconstruct an independent live
 * render of it (used to clone the outgoing look into a second layer during a
 * transition). Matches what the store holds and what a saved scene captures.
 */
export interface SceneDescription {
  params: VisualParams
  backgroundVideoUrl: string | null
  backgroundImageUrl: string | null
  videoLab: VideoLabSettings
  primitiveLab: VideoLabSettings
  textureUrl: string | null
  zoneReaction: ZoneReactionConfig
  camera: CameraPose | null
}
